import React from 'react';
import {
  useParams,
  useSearchParams
} from 'react-router-dom';

const money = value =>
  '₦' +
  Number(value || 0)
    .toLocaleString(
      'en-NG',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );

async function publicApi(
  path,
  options={}
) {
  const response = await fetch(
    '/api/public/invoice-payments' +
      path,
    {
      ...options,
      headers: {
        'Content-Type':
          'application/json',
        ...(options.headers || {})
      },
      body: options.body
        ? JSON.stringify(options.body)
        : undefined,
      cache: 'no-store'
    }
  );

  const data =
    await response.json();

  if (!response.ok) {
    const error = new Error(
      data.error ||
      'The request could not be completed'
    );

    error.code = data.code;
    throw error;
  }

  return data;
}

export default function InvoicePaymentPublic() {
  const { token } = useParams();
  const [searchParams] =
    useSearchParams();

  const [invoice, setInvoice] =
    React.useState(null);

  const [selectedOption, setSelectedOption] =
    React.useState('');

  const [channel, setChannel] =
    React.useState('local');

  const [loading, setLoading] =
    React.useState(true);

  const [working, setWorking] =
    React.useState(false);

  const [error, setError] =
    React.useState('');

  const [success, setSuccess] =
    React.useState(null);

  const loadInvoice =
    React.useCallback(async () => {
      const response =
        await publicApi(
          '/' +
          encodeURIComponent(token)
        );

      const record = response.data;

      setInvoice(record);

      if (
        record.payment_options?.length > 0
      ) {
        setSelectedOption(
          current =>
            current ||
            record
              .payment_options[0]
              .key
        );
      }

      return record;
    }, [token]);

  React.useEffect(() => {
    let active = true;

    const start = async () => {
      setLoading(true);
      setError('');

      try {
        const reference =
          searchParams.get(
            'reference'
          ) ||
          searchParams.get(
            'trxref'
          );

        if (reference) {
          const verification =
            await publicApi(
              '/' +
              encodeURIComponent(token) +
              '/verify'
            );

          if (!active) return;

          setSuccess(
            verification.data
          );
        }

        if (!active) return;

        await loadInvoice();
      } catch (requestError) {
        if (!active) return;

        setError(
          requestError.message
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      active = false;
    };
  }, [
    token,
    searchParams,
    loadInvoice
  ]);

  const payWithPaystack = async () => {
    if (
      !selectedOption ||
      ![
        'local',
        'international'
      ].includes(channel) ||
      working
    ) {
      return;
    }

    setWorking(true);
    setError('');

    try {
      const response =
        await publicApi(
          '/' +
          encodeURIComponent(token) +
          '/initialize',
          {
            method: 'POST',
            body: {
              payment_option:
                selectedOption,
              payment_origin:
                channel
            }
          }
        );

      if (
        response.data?.status ===
        'paid'
      ) {
        await loadInvoice();
        setSuccess({
          status: 'paid'
        });
        return;
      }

      const authorizationUrl =
        response.data
          ?.authorization_url;

      if (!authorizationUrl) {
        throw new Error(
          'The secure payment page could not be opened'
        );
      }

      window.location.assign(
        authorizationUrl
      );
    } catch (requestError) {
      setError(
        requestError.message
      );
    } finally {
      setWorking(false);
    }
  };

  const selected =
    invoice?.payment_options?.find(
      option =>
        option.key ===
        selectedOption
    );

  const selectedQuote =
    channel === 'bank'
      ? null
      : selected?.quotes?.[channel];

  if (loading) {
    return (
      <PageShell>
        <div style={{
          textAlign:'center',
          padding:'50px 20px',
          color:'#556B2F'
        }}>
          Loading secure invoice...
        </div>
      </PageShell>
    );
  }

  if (error && !invoice) {
    return (
      <PageShell>
        <Notice
          type="error"
          title="Payment unavailable"
        >
          {error}
        </Notice>
      </PageShell>
    );
  }

  if (
    success?.status === 'paid' ||
    invoice?.status === 'paid' ||
    invoice?.balance_due <= 0
  ) {
    return (
      <PageShell>
        <Notice
          type="success"
          title="Payment confirmed"
        >
          Thank you. Your payment has
          been verified and applied to
          invoice{' '}
          <strong>
            {invoice?.invoice_number ||
             success?.invoice_number ||
             ''}
          </strong>.
        </Notice>

        {success?.amount && (
          <div style={{
            marginTop:16,
            textAlign:'center',
            color:'#3B2A21'
          }}>
            Amount received:{' '}
            <strong>
              {money(success.amount)}
            </strong>
          </div>
        )}
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div style={{
        marginBottom:22,
        textAlign:'center'
      }}>
        <div style={{
          color:'#8A8A8A',
          fontSize:11,
          fontWeight:700,
          letterSpacing:'.1em',
          textTransform:'uppercase'
        }}>
          Secure invoice payment
        </div>

        <h1 style={{
          margin:'7px 0 4px',
          fontFamily:
            'Cormorant Garamond, Georgia, serif',
          color:'#3B2A21',
          fontSize:30
        }}>
          {invoice.invoice_number}
        </h1>

        <div style={{
          color:'#555',
          fontSize:14
        }}>
          {invoice.customer_name}
        </div>
      </div>

      {error && (
        <Notice
          type="error"
          title="Payment could not continue"
        >
          {error}
        </Notice>
      )}

      <div style={{
        display:'grid',
        gridTemplateColumns:
          'repeat(3,minmax(0,1fr))',
        gap:8,
        marginBottom:22
      }}>
        <Summary
          label="Invoice total"
          value={money(invoice.total)}
        />
        <Summary
          label="Amount paid"
          value={money(
            invoice.amount_paid
          )}
        />
        <Summary
          label="Balance due"
          value={money(
            invoice.balance_due
          )}
          alert
        />
      </div>

      <SectionTitle>
        Choose payment amount
      </SectionTitle>

      <div style={{
        display:'grid',
        gap:10,
        marginBottom:22
      }}>
        {(invoice.payment_options || [])
          .map(option => (
            <label
              key={option.key}
              style={{
                display:'flex',
                justifyContent:
                  'space-between',
                alignItems:'center',
                gap:12,
                border:
                  selectedOption ===
                    option.key
                    ? '2px solid #556B2F'
                    : '1px solid #D8CEBF',
                borderRadius:10,
                padding:'14px 16px',
                cursor:'pointer',
                background:
                  selectedOption ===
                    option.key
                    ? '#F2EFE7'
                    : '#fff'
              }}
            >
              <span style={{
                display:'flex',
                alignItems:'center',
                gap:10
              }}>
                <input
                  type="radio"
                  name="payment_option"
                  value={option.key}
                  checked={
                    selectedOption ===
                    option.key
                  }
                  onChange={() =>
                    setSelectedOption(
                      option.key
                    )
                  }
                />
                <strong style={{
                  color:'#3B2A21',
                  fontSize:14
                }}>
                  {option.label}
                </strong>
              </span>

              <strong style={{
                color:'#556B2F',
                fontSize:16
              }}>
                {money(option.amount)}
              </strong>
            </label>
          ))}
      </div>

      <SectionTitle>
        Choose how you are paying
      </SectionTitle>

      <div style={{
        display:'grid',
        gridTemplateColumns:
          'repeat(auto-fit,minmax(150px,1fr))',
        gap:10,
        marginBottom:18
      }}>
        <Channel
          selected={
            channel === 'local'
          }
          onClick={() =>
            setChannel('local')
          }
          title="Nigerian payment"
          detail="Nigerian bank transfer, bank or USSD"
        />

        <Channel
          selected={
            channel ===
              'international'
          }
          onClick={() =>
            setChannel(
              'international'
            )
          }
          title="International payment"
          detail="Foreign card, Apple Pay or Google Pay"
        />

        <Channel
          selected={
            channel === 'bank'
          }
          onClick={() =>
            setChannel('bank')
          }
          title="Manual transfer"
          detail="Pay directly into our Moniepoint account"
        />
      </div>

      {channel !== 'bank' && (
        <div style={{
          border:'1px solid #D8CEBF',
          borderRadius:10,
          padding:'14px 16px',
          background:'#F9F8F5',
          marginBottom:16
        }}>
          <div style={{
            display:'flex',
            justifyContent:
              'space-between',
            gap:12,
            marginBottom:8,
            fontSize:13,
            color:'#555'
          }}>
            <span>Invoice payment</span>
            <strong>
              {money(
                selectedQuote
                  ?.invoice_amount
              )}
            </strong>
          </div>

          <div style={{
            display:'flex',
            justifyContent:
              'space-between',
            gap:12,
            marginBottom:10,
            fontSize:13,
            color:'#555'
          }}>
            <span>
              {channel ===
                'international'
                ? 'International processing charge'
                : 'Nigerian processing charge'}
            </span>
            <strong>
              {money(
                selectedQuote
                  ?.processing_fee
              )}
            </strong>
          </div>

          <div style={{
            display:'flex',
            justifyContent:
              'space-between',
            gap:12,
            paddingTop:10,
            borderTop:
              '1px solid #D8CEBF',
            color:'#3B2A21',
            fontSize:15
          }}>
            <strong>
              Total Paystack charge
            </strong>
            <strong style={{
              color:'#556B2F'
            }}>
              {money(
                selectedQuote?.total
              )}
            </strong>
          </div>
        </div>
      )}

      {channel ===
        'international' && (
        <Notice
          type="info"
          title="International payment"
        >
          Paystack will charge your
          card in naira. Your bank
          converts the amount into
          your card currency. The
          processing charge shown
          above protects the invoice
          value from international
          card charges.
        </Notice>
      )}

      {channel === 'local' && (
        <div style={{
          color:'#6F6F6F',
          fontSize:11,
          lineHeight:1.6,
          marginBottom:14,
          textAlign:'center'
        }}>
          This option accepts Nigerian
          bank transfer, bank payment
          and USSD only. Cards and
          international wallets are
          not available on this route.
        </div>
      )}

      {channel !== 'bank' ? (
        <div>
          <button
            type="button"
            disabled={
              working ||
              !selected ||
              !selectedQuote
            }
            onClick={payWithPaystack}
            style={{
              width:'100%',
              border:0,
              borderRadius:10,
              padding:'14px 18px',
              background:
                working ||
                !selected ||
                !selectedQuote
                  ? '#A8A8A8'
                  : '#556B2F',
              color:'#fff',
              fontSize:15,
              fontWeight:700,
              cursor:
                working ||
                !selected ||
                !selectedQuote
                  ? 'not-allowed'
                  : 'pointer'
            }}
          >
            {working
              ? 'Opening secure payment...'
              : selectedQuote
                ? 'Pay ' +
                  money(
                    selectedQuote.total
                  ) +
                  ' securely'
                : 'Select an amount'}
          </button>

          <div style={{
            textAlign:'center',
            color:'#8A8A8A',
            fontSize:11,
            marginTop:10,
            lineHeight:1.5
          }}>
            Your invoice is credited
            with the invoice payment
            amount. Processing charges
            are recorded separately.
            Payment is applied only
            after server verification.
          </div>
        </div>
      ) : (
        <BankTransfer
          details={
            invoice.bank_transfer
          }
          amount={
            selected?.amount
          }
          invoiceNumber={
            invoice.invoice_number
          }
        />
      )}

      <div style={{
        marginTop:22,
        paddingTop:16,
        borderTop:
          '1px solid #E8E0D4',
        color:'#8A8A8A',
        fontSize:11,
        lineHeight:1.6,
        textAlign:'center'
      }}>
        Never send cash or payment
        credentials to anyone claiming
        to process this payment.
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div style={{
      minHeight:'100vh',
      background:'#F2EFE7',
      padding:'28px 14px',
      fontFamily:
        'Inter, Arial, sans-serif'
    }}>
      <div style={{
        maxWidth:620,
        margin:'0 auto'
      }}>
        <div style={{
          background:'#3B2A21',
          color:'#F2EFE7',
          padding:'20px 24px',
          borderRadius:'12px 12px 0 0'
        }}>
          <div style={{
            fontFamily:
              'Cormorant Garamond, Georgia, serif',
            fontSize:24,
            fontWeight:700
          }}>
            BLOANS
          </div>
          <div style={{
            fontSize:11,
            color:'#D8CEBF',
            marginTop:3
          }}>
            Designed for brands, Made
            for moments.
          </div>
        </div>

        <main style={{
          background:'#fff',
          border:
            '1px solid #E8E0D4',
          borderTop:0,
          borderRadius:'0 0 12px 12px',
          padding:'26px 24px',
          boxShadow:
            '0 10px 30px rgba(59,42,33,.08)'
        }}>
          {children}
        </main>
      </div>
    </div>
  );
}

function Summary({
  label,
  value,
  alert
}) {
  return (
    <div style={{
      background:'#F9F8F5',
      borderRadius:8,
      padding:11,
      textAlign:'center'
    }}>
      <div style={{
        color:'#8A8A8A',
        fontSize:9,
        fontWeight:700,
        letterSpacing:'.06em',
        textTransform:'uppercase',
        marginBottom:5
      }}>
        {label}
      </div>
      <strong style={{
        color:
          alert
            ? '#8F2E24'
            : '#3B2A21',
        fontSize:13
      }}>
        {value}
      </strong>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      color:'#556B2F',
      fontSize:11,
      fontWeight:800,
      letterSpacing:'.08em',
      textTransform:'uppercase',
      marginBottom:9
    }}>
      {children}
    </div>
  );
}

function Channel({
  selected,
  onClick,
  title,
  detail
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign:'left',
        border:
          selected
            ? '2px solid #556B2F'
            : '1px solid #D8CEBF',
        borderRadius:10,
        padding:13,
        background:
          selected
            ? '#F2EFE7'
            : '#fff',
        cursor:'pointer'
      }}
    >
      <strong style={{
        display:'block',
        color:'#3B2A21',
        fontSize:13
      }}>
        {title}
      </strong>
      <span style={{
        display:'block',
        color:'#8A8A8A',
        fontSize:10,
        marginTop:3
      }}>
        {detail}
      </span>
    </button>
  );
}

function BankTransfer({
  details,
  amount,
  invoiceNumber
}) {
  const copyDetails = async () => {
    const text = [
      'Account Name: ' +
        details.account_name,
      'Account Number: ' +
        details.account_number,
      'Bank: ' + details.bank,
      'Amount: ' + money(amount),
      'Reference: ' +
        invoiceNumber
    ].join('\n');

    try {
      await navigator.clipboard
        .writeText(text);

      window.alert(
        'Bank details copied.'
      );
    } catch {
      window.alert(
        'Please copy the bank details manually.'
      );
    }
  };

  return (
    <div style={{
      border:
        '1px solid #D8CEBF',
      borderRadius:10,
      padding:16,
      background:'#F9F8F5'
    }}>
      {[
        [
          'Account Name',
          details.account_name
        ],
        [
          'Account Number',
          details.account_number
        ],
        ['Bank', details.bank],
        [
          'Amount',
          money(amount)
        ],
        [
          'Payment Reference',
          invoiceNumber
        ]
      ].map(([label, value]) => (
        <div
          key={label}
          style={{
            display:'flex',
            justifyContent:
              'space-between',
            gap:15,
            borderBottom:
              '1px solid #E8E0D4',
            padding:'7px 0',
            fontSize:13
          }}
        >
          <span style={{
            color:'#8A8A8A'
          }}>
            {label}
          </span>
          <strong style={{
            color:'#3B2A21',
            textAlign:'right'
          }}>
            {value}
          </strong>
        </div>
      ))}

      <button
        type="button"
        onClick={copyDetails}
        style={{
          width:'100%',
          marginTop:13,
          border:
            '1px solid #556B2F',
          borderRadius:8,
          padding:'10px 14px',
          background:'#fff',
          color:'#556B2F',
          fontWeight:700,
          cursor:'pointer'
        }}
      >
        Copy bank details
      </button>

      <div style={{
        marginTop:12,
        color:'#555',
        fontSize:11,
        lineHeight:1.6
      }}>
        After payment, send proof to{' '}
        <strong>
          {details.confirmation_phone}
        </strong>.
        Bank transfers are confirmed
        manually and will not update
        this invoice until verified by
        Bloans.
      </div>
    </div>
  );
}

function Notice({
  type,
  title,
  children
}) {
  const success =
    type === 'success';

  return (
    <div style={{
      border:
        '1px solid ' +
        (
          success
            ? '#A9D6B1'
            : '#F0B8B2'
        ),
      background:
        success
          ? '#E8F5EB'
          : '#FDEAEA',
      color:
        success
          ? '#2E6B3A'
          : '#8F2E24',
      borderRadius:10,
      padding:18,
      lineHeight:1.6
    }}>
      <strong style={{
        display:'block',
        fontSize:16,
        marginBottom:5
      }}>
        {title}
      </strong>
      <div style={{fontSize:13}}>
        {children}
      </div>
    </div>
  );
}
